import lang from 'lang'
import settingsConfig from '@/modules/settings/config'
import request from '@/modules/base/util/request'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { pick } from 'lodash'
import { errorCodes } from '@/modules/base/util/constants'

const ACTION_PREFIX = 'settings/company'
const SHOW_LOADER = `${ACTION_PREFIX}/SHOW_LOADER`
const HIDE_LOADER = `${ACTION_PREFIX}/HIDE_LOADER`
const SET_COMPANY_DATA = `${ACTION_PREFIX}/SET_COMPANY_DATA`

const initialState = {
  isLoading: false,
  isSaving: false,
  companyName: '',
  city: '',
  street: '',
  zipCode: '',
  email: '',
  website: '',
  phone1: '',
  phone2: '',
  mobile: '',
  fax: '',
  companyAddress: '',
  taxNumber: '',
  vatNumber: '',
  wIdNr: '',
  taxOfficeId: '',
  countryIso: 'DE',
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SHOW_LOADER:
      return { ...state, isLoading: true }

    case HIDE_LOADER:
      return { ...state, isLoading: false }

    case SET_COMPANY_DATA: {
      const { data } = action
      return { ...state, ...data }
    }

    default:
      return state
  }
}

// action creators

const showLoader = () => {
  return {
    type: SHOW_LOADER,
  }
}

const hideLoader = () => {
  return {
    type: HIDE_LOADER,
  }
}

export const setCompanyData = (data) => {
  return {
    type: SET_COMPANY_DATA,
    data,
  }
}

export const fetchCompanyData = (collaborationId) => {
  const headers = {}
  if (collaborationId) headers['collaboration-id'] = collaborationId

  return (dispatch, getState) => {
    const { isLoading } = getState().settings.company
    if (!isLoading) {
      dispatch(showLoader())
      request(settingsConfig.account, { auth: true, isConcurrent: true, headers })
        .then(({ body: { data } }) => {
          dispatch(setCompanyData(prepareDataForSetAfterLoad({}, { ...data })))
        })
        .catch(() => {
          toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
        })
        .done(() => dispatch(hideLoader()))
    }
  }
}

export const submitCompanyData = (formData, keepTaxDetails = true, collaborationId, silentToast = false) => {
  const headers = {}
  if (collaborationId) headers['collaboration-id'] = collaborationId

  return (dispatch) => {
    dispatch(setCompanyData(formData))
    const data = prepareDataForSubmit(formData, keepTaxDetails)
    request(settingsConfig.account, { method: 'POST', auth: true, data, headers })
      .then(({ body: { data } }) => {
        if (!silentToast) {
          toast.showToast({ message: lang.settings.company.updateSuccessMessage })
        }
        dispatch(setCompanyData(prepareDataForSetAfterLoad({}, data)))
      })
      .catch((error) => {
        let errorMessage = lang.defaultErrorMessage
        if (error?.body?.meta?.tenant?.[0]?.code === errorCodes.NOT_ALLOWED) {
          errorMessage = lang.settings.company.errors.isDemo
        }

        toast.showToast({ type: 'error', message: errorMessage })
      })
      .finally(() => dispatch(hideLoader()))
  }
}

const prepareDataForSubmit = (data, keepTaxDetails) => {
  const newCompanyAddress = pick(data.companyAddress || data, [
    'companyName',
    'city',
    'street',
    'zipCode',
    'countryIso',
    'email',
    'website',
    'phone1',
    'phone2',
    'mobile',
    'fax',
    'iban',
    'bic',
    'bankAccountHolder',
    'ownerLastName',
    'ownerFirstName',
    'email',
  ])

  const parsedData = {
    companyAddress: newCompanyAddress,
    beginningOfFiscalYear: data.beginningOfFiscalYear,
    legalFormNames: data.legalFormNames,
    vatReturnPeriods: data.vatReturnPeriods,
    profitDerminationNames: data.profitDerminationNames,
    clientTenantId: data.clientTenantId,
    onboardingTesterVisited: data.onboardingTesterVisited,
    onboardingTesterFinished: data.onboardingTesterFinished,
  }
  if (keepTaxDetails) {
    parsedData.taxNumber = data.taxNumber
    parsedData.vatNumber = data.vatNumber
    parsedData.wIdNr = data.wIdNr
    parsedData.taxOfficeId = data.taxOfficeId
  }

  return parsedData
}

const prepareDataForSetAfterLoad = (formData, data) => {
  if (data?.companyAddress && data?.companyAddress.hasOwnProperty('companyAddress')) {
    delete data.companyAddress.companyAddress
  }

  return {
    ...formData,
    ...data.companyAddress,
    taxNumber: data.taxNumber || '',
    vatNumber: data.vatNumber || '',
    wIdNr: data.wIdNr || '',
    taxOfficeId: data.taxOfficeId || '',
    countryIso: data?.countryIso || data?.companyAddress?.countryIso || 'DE',
    isSmallBusiness: data.isSmallBusiness,
    isSubjectToImputedTaxation: data.isSubjectToImputedTaxation,
    beginningOfFiscalYear: data.beginningOfFiscalYear,
    legalFormNames: data.legalFormNames,
    vatReturnPeriods: data.vatReturnPeriods,
    profitDerminationNames: data.profitDerminationNames,
    disabled: data.disabled,
    onboardingTesterVisited: data.onboardingTesterVisited,
    onboardingTesterFinished: data.onboardingTesterFinished,
    defaultRevenueAccount: data.defaultRevenueAccount,
  }
}
